import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate } from "../utils";

import { usePymbleApi } from "./usePymbleApi";

const COVER_TEACHERS_END_POINT = "/timetable/admin/lesson/covers/";

export type CoverTeacher = {
  _id?: string;
  cid?: string;
  Name?: string;
  CoverCount?: number;
  email?: string;
};

/**
 * Hook for separate loading of on call teachers for a period
 * for components
 */
export function useCoverTeachers(
  startDate: string | null = null,
  endDate: string | null = null,
  teacherId: string | null = null
) {
  const [myCoverTeachers, setMyCoverTeachers] = useState<any>([]);
  const [myCoverTeachersLoaded, setMyCoverTeachersLoaded] = useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the list from api
  let queryKey = [
    "my-cover-teachers",
    startDate, teacherId,
  ];

  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: () =>
      callApi(
        "GET",
        COVER_TEACHERS_END_POINT +"?" +
        (startDate ? "start_date=" + formatDate(startDate) + "&" : "") +
        (endDate ? "end_date=" + endDate + "&" : "") +
        (teacherId ? "teacher_id=" + teacherId + "&" : "")
      ),
    enabled: isAuthenticated,
    staleTime: 1000, refetchOnWindowFocus: true,
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data && !isLoading) {
      let list = preProcessTeachers(data);

      // Sort by name
      list = sortTeachers(list);

      if (list) {
        setMyCoverTeachers(list);
      }
      setMyCoverTeachersLoaded(true);
    }
  }, [data, isLoading]);

  const refreshMyCoverTeachers = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  // Only sort by name at the moment
  const sortTeachers = (data: CoverTeacher[]) => {
    return data.sort(function (a: CoverTeacher, b: CoverTeacher) {
      try {
        if (a && a?.Name && b && b?.Name) {
          // Sort by Name
          var NameA = a?.Name;
          var NameB = b?.Name;
          if (NameA < NameB) {
            return -1;
          }
          if (NameA > NameB) {
            return 1;
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Decorate as required
  const preProcessTeachers = (classdata: any) => {
    return classdata.map((c: any) => {
      const t = { ...c, _id: c.cid };
      return t;
    });
  };

  return {
    isError,
    error,
    isLoading,
    myCoverTeachers,
    refreshMyCoverTeachers,
    myCoverTeachersLoaded,
  };
}
